import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  IBusinessDataStep,
  ICardVerificationForm,
  ICardVerificationFormBranch,
  IDistributorDataStep,
  INoteChangeVerification,
  IPayloadRegistrationVerification,
} from '../../../../interfaces/detail-registration.interface';
import { DetailRegistrationDistributorService } from '../detail-registration.service';
import {
  branchBodyData,
  businessBodyData,
  distributorDetailBodyData,
  EnumCardVerificationForm,
  EnumCardVerificationSection,
  managerBodyData,
  npwpBodyData,
  ownerBodyData,
  scopeBodyData,
} from './distributor-data.data';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';

@Component({
  selector: 'app-distributor-data',
  templateUrl: './distributor-data.component.html',
  styleUrls: ['./distributor-data.component.scss'],
})
export class DistributorDataComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() isViewOnly = false;
  @Input() isOnlyChangeSubArea = false;

  bodyDataScope: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataDistributor: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataOwner: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataManager: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataBusiness: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataNpwp: BehaviorSubject<ICardVerificationForm[]> = new BehaviorSubject<ICardVerificationForm[]>([]);
  bodyDataBranch: BehaviorSubject<ICardVerificationFormBranch[]> = new BehaviorSubject<ICardVerificationFormBranch[]>([]);

  dataValue: BehaviorSubject<IDistributorDataStep> = new BehaviorSubject<IDistributorDataStep>({} as IDistributorDataStep);
  dataValueBusiness: BehaviorSubject<IBusinessDataStep> = new BehaviorSubject<IBusinessDataStep>({} as IBusinessDataStep);
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  revisionNoteData: IPayloadRegistrationVerification;
  revisionType: EnumDistributorRevisionSession;

  get ShowBadge() {
    return this.detailRegistrationService.showBadgePendingDetail();
  }

  constructor(public detailRegistrationService: DetailRegistrationDistributorService, private activatedRoute: ActivatedRoute, private ref: ChangeDetectorRef) {
    const statusTab = this.activatedRoute.snapshot.params.tab;
    this.revisionType = this.detailRegistrationService.getTypeRevision(statusTab);
  }

  ngOnInit() {
    this.getRevisionNoteData();
    this.getData();
  }

  getRevisionNoteData() {
    this.revisionNoteData = this.detailRegistrationService.getSessionRevisionNote(this.revisionType) ?? ({} as IPayloadRegistrationVerification);
  }

  getData() {
    this.isLoading.next(true);
    this.detailRegistrationService.getDistributorDataStep().subscribe((value) => {
      if (!value) return;
      this.dataValue.next(value);
      !this.isViewOnly && this.handleValidationForm();
      this.generateBodyData(value);
      this.isLoading.next(false);
    });

    if (this.isViewOnly) {
      this.detailRegistrationService.getBusinessDataStep().subscribe((value) => {
        if (!value) return;
        this.dataValueBusiness.next(value);
        this.generateBodyDataBusiness();
        this.ref.detectChanges();
      });
    }
  }

  generateBodyData(data: IDistributorDataStep) {
    const { scope, distributor_detail, owner, manager, npwp_information, business_branch } = data;

    const _scopeBody: ICardVerificationForm[] = scopeBodyData;
    const _scope = this.detailRegistrationService.generateBodyData(scope, _scopeBody);
    this.bodyDataScope.next(_scope);

    const _distributorDetailBody: ICardVerificationForm[] = distributorDetailBodyData;
    const _distributorDetail = this.detailRegistrationService.generateBodyData(distributor_detail, _distributorDetailBody);
    this.bodyDataDistributor.next(_distributorDetail);

    const _ownerBody: ICardVerificationForm[] = ownerBodyData;
    const _owner = this.detailRegistrationService.generateBodyData(owner, _ownerBody);
    this.bodyDataOwner.next(_owner);

    const _managerBody: ICardVerificationForm[] = managerBodyData;
    const _manager = this.detailRegistrationService.generateBodyData(manager, _managerBody);
    this.bodyDataManager.next(_manager);

    // Is View Only
    const _npwpBody: ICardVerificationForm[] = npwpBodyData;
    const _npwp = this.detailRegistrationService.generateBodyData(npwp_information, _npwpBody);
    this.bodyDataNpwp.next(_npwp);

    const _branchBody: ICardVerificationForm[] = branchBodyData;
    const _branchList = business_branch.branch_list.flatMap((value) => {
      // Generate a new object for each branch
      const _branchTmp = [...this.detailRegistrationService.generateBodyData(value, _branchBody)];
      const name = _branchTmp.find((val: any) => val.key === 'name')?.value ?? ''; // Safeguard for undefined
      return {
        label: name,
        data: _branchTmp, // Ensure _branchTmp is a new object/array
      };
    });

    this.bodyDataBranch.next(_branchList);
  }

  generateBodyDataBusiness() {
    const { business_information } = this.dataValueBusiness.value;

    const _businessBody: ICardVerificationForm[] = businessBodyData('establishment_deed_url');
    const _business = this.detailRegistrationService.generateBodyData(business_information, _businessBody);
    this.bodyDataBusiness.next(_business);
  }

  handleValidationForm() {
    this.dataValue.subscribe((value) => {
      if (Object.keys(value).length <= 0) return;
      const { scope, distributor_detail, owner, manager, business_branch } = value;
      const { revision_note } = this.revisionNoteData;

      const scopeControl = this.form.get('scope');
      const distributorDetailControl = this.form.get('distributor_detail');
      const ownerControl = this.form.get('owner');
      const managerControl = this.form.get('manager');
      const businessBranchControl = this.form.get('business_branch');
      const onlyChangeSubArea = this.isOnlyChangeSubArea;

      if (scopeControl) {
        if (this.detailRegistrationService.checkValidationFormStepper(scopeControl, scope) && this.detailRegistrationService.isVerifiedAndRevision(scope)) {
          scopeControl.clearValidators();
        } else {
          scopeControl.setValidators([Validators.required]);
          scopeControl.setValue(revision_note.scope);
        }
        scopeControl.updateValueAndValidity();
      }
      if (distributorDetailControl) {
        if (
          this.detailRegistrationService.checkValidationFormStepper(distributorDetailControl, distributor_detail) &&
          this.detailRegistrationService.isVerifiedAndRevision(distributor_detail)
        ) {
          distributorDetailControl.clearValidators();
        } else {
          distributorDetailControl.setValidators([Validators.required]);
          distributorDetailControl.setValue(revision_note.distributor_detail);
        }
        distributorDetailControl.updateValueAndValidity();
      }
      if (ownerControl) {
        if (onlyChangeSubArea || (this.detailRegistrationService.isVerifiedAndRevision(owner) && this.detailRegistrationService.checkValidationFormStepper(ownerControl, owner))) {
          ownerControl.clearValidators();
        } else {
          ownerControl.setValidators([Validators.required]);
          ownerControl.setValue(revision_note.owner);
        }
        ownerControl.updateValueAndValidity();
      }
      if (managerControl) {
        if (
          onlyChangeSubArea ||
          (this.detailRegistrationService.isVerifiedAndRevision(manager) && this.detailRegistrationService.checkValidationFormStepper(managerControl, manager))
        ) {
          managerControl.clearValidators();
        } else {
          managerControl.setValidators([Validators.required]);
          managerControl.setValue(revision_note.manager);
        }
        managerControl.updateValueAndValidity();
      }
      if (businessBranchControl) {
        const _check =
          onlyChangeSubArea ||
          (this.detailRegistrationService.isVerifiedAndRevision(business_branch) &&
            this.detailRegistrationService.checkValidationFormStepper(businessBranchControl, business_branch));
        const isHaveValue = !!this.bodyDataBranch.value.length;
        if (_check || !isHaveValue) {
          businessBranchControl.clearValidators();
        } else {
          businessBranchControl.setValidators([Validators.required]);
          businessBranchControl.setValue(revision_note.business_branch);
        }
        businessBranchControl.updateValueAndValidity();
      }
    });
  }

  noteChanged(e: INoteChangeVerification) {
    this.detailRegistrationService.setRevisionData(this.revisionNoteData, e, this.revisionType);
    this.getRevisionNoteData();
  }

  noteValue(type: EnumCardVerificationSection) {
    if (this.isViewOnly) return;
    const { revision_note } = this.revisionNoteData;
    !this.isViewOnly && this.handleValidationForm();
    return revision_note && revision_note[type.toLowerCase() as keyof typeof revision_note];
  }

  afterActionVerification(event: string | null) {
    if (event === null) {
      this.getRevisionNoteData();
    }
    this.getData();
  }

  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
  protected readonly Array = Array;
}
